import React from "react";

import {
	EightySixContext,
	type EightySixContextValue,
} from "./eighty-six-context";

/**
 * HA-591 — the 86 state, available to any tile without threading it through the grid.
 *
 * <para>A context of its own rather than a field on the epos context: that hook is shared by every
 * screen in the terminal and owns the order, the tabs and the payment flow. 86 is read by the
 * product tiles and the keypad dialog and by nothing else, and widening the shared hook for it
 * would make every consumer re-render on a stock change none of them care about.</para>
 *
 * <para><b>Fail open is for a failed QUERY, never for a missing provider.</b> Those are different
 * failures and they had the same handling, which is how the first cut of this shipped a provider
 * nothing mounted: every tile read a permissive default, sold everything, and looked correct.
 * A mounted provider whose status call fails still yields no statuses, so the menu keeps selling
 * and the send has the final say — that is recoverable. A MISSING provider is a wiring mistake, so
 * `useEightySix` throws and the first render fails, in dev and in any test, rather than silently
 * disabling the feature.</para>
 */
export function EightySixProvider({
	children,
	...value
}: React.PropsWithChildren<EightySixContextValue>) {
	const memoised = React.useMemo(
		() => ({
			statuses: value.statuses,
			permissions: value.permissions,
			apply: value.apply,
			pending: value.pending,
		}),
		[value.statuses, value.permissions, value.apply, value.pending],
	);

	return <EightySixContext value={memoised}>{children}</EightySixContext>;
}
